import { z } from "zod";
import { idSchema } from "./fields";

export const userRoleSchema = z.enum(["admin", "veteran"]);
export type UserRole = z.infer<typeof userRoleSchema>;

/** Accounts are identified by email address, compared case-insensitively. */
export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(1, "Email is required.")
  .max(254, "Email must be at most 254 characters.")
  .pipe(z.email({ error: "Enter a valid email address." }));

export const PASSWORD_MIN_LENGTH = 10;

export const passwordSchema = z
  .string()
  .min(
    PASSWORD_MIN_LENGTH,
    `Password must be at least ${PASSWORD_MIN_LENGTH} characters.`
  )
  .max(200, "Password must be at most 200 characters.");

export const displayNameSchema = z
  .string()
  .trim()
  .min(1, "Name is required.")
  .max(100, "Name must be at most 100 characters.");

/**
 * - active: has set a password and can sign in (if not deactivated)
 * - invited: waiting on an unexpired invitation
 * - inviteExpired: never accepted, and the invitation link has lapsed
 */
export const accountStatusSchema = z.enum([
  "active",
  "invited",
  "inviteExpired",
]);
export type AccountStatus = z.infer<typeof accountStatusSchema>;

export const userSchema = z.object({
  id: idSchema,
  email: z.string(),
  displayName: z.string(),
  role: userRoleSchema,
  isActive: z.boolean(),
  status: accountStatusSchema,
  /** When the user signed the current waiver version, if they have. */
  waiverSignedAt: z.date().optional(),
  createdAt: z.date(),
});
export type User = z.infer<typeof userSchema>;

/** What an admin enters to invite someone. */
export const inviteUserSchema = z.object({
  email: emailSchema,
  role: userRoleSchema,
});
export type InviteUser = z.infer<typeof inviteUserSchema>;

export const updateUserSchema = z.object({
  displayName: displayNameSchema,
  role: userRoleSchema,
  isActive: z.boolean(),
});
export type UpdateUser = z.infer<typeof updateUserSchema>;

/** The signed-in user as seen by the browser. */
export const currentUserSchema = z.object({
  id: idSchema,
  email: z.string(),
  displayName: z.string(),
  role: userRoleSchema,
  waiverSigned: z.boolean(),
});
export type CurrentUser = z.infer<typeof currentUserSchema>;

export const loginSchema = z.object({
  email: z.string().trim().toLowerCase().min(1, "Email is required.").max(254),
  password: z.string().min(1, "Password is required.").max(200),
});
